/**
 * Backoffice CRM: User Diagnostics & Developer Mode Controller
 */

import { BADGE, BADGE_DOT, BUTTON, SEGMENT } from "./ui/classes.js";
import { icon, solidIcon } from "./ui/icons.js";
import { copyAttributes } from "./ui/copy.js";
import { familyMessageHTML, lastDeviceHTML } from "./familyDetailsPanel.js";
import { createFamilyCard } from "./familyCard.js";
import { familyHref } from "./familiesController.js";
import { historyHref } from "./historyController.js";
import { bindFamilyImportDialog } from "./familyImport.js";
import { bindFamilyRecipeDrawer } from "./familyRecipe.js";
import { bindFamilyImportBatchDrawer } from "./familyImportBatch.js";
import { autoRenewOff, offerText } from "./utils/subscriptionOffer.js";
import { initTestUsersPanel, reasonBadgesHTML } from "./testUsersPanel.js";
import { offerStatusBadgeHTML, offerStatusRows } from "./utils/leadOfferStatus.js";

export { lastDeviceHTML };

let activeUserRecord = null;
let firestoreCtx = null;
let activeHeatmapFeature = "overall";
// The family card for the user on screen (#1459), shared with the Families page (#1809).
const familyCard = createFamilyCard({
  containerId: "userFamilyCardContainer",
  getContext: () => firestoreCtx,
  getCurrentUid: () => activeUserRecord?.uid || null,
  onLookupUser: (uid) => {
    const input = document.getElementById("userSearchInput");
    if (input) input.value = uid;
    performUserSearch(uid);
  },
  missingHTML: (familyId) => {
    const hasFamilyField = Boolean(activeUserRecord?.family_id || activeUserRecord?.familyId);
    return familyMessageHTML(hasFamilyField ? familyId : null,
      hasFamilyField ? "The family document is missing" : "Not in a family",
      hasFamilyField
        ? `<code class="font-mono text-xs">users/…/family_id</code> points at <code class="font-mono text-xs">families/${escapeHTML(familyId)}</code>, which does not exist.`
        : "This user has no <code class=\"font-mono text-xs\">family_id</code> and owns no family under their user ID; they have probably not finished onboarding.");
  }
});

// A stored heatmap may still count the retired Sunday Surge (#2451): it has no button of its own, and Overall Activity
// counts it with the rest.
export const HEATMAP_FEATURES = [
  { id: "overall", label: "Overall Activity", icon: "📊" },
  { id: "cooking_recipe", label: "Cooking", icon: "🍳" },
  { id: "meal_planning", label: "Meal Planning", icon: "📅" },
  { id: "app_open", label: "App Open", icon: "📱" },
  { id: "grocery_shopping", label: "Grocery", icon: "🛒" }
];

export const HEATMAP_DAYS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" }
];

/**
 * Loads the users.html partial template into the container
 */
export async function loadUsersTemplate() {
  const container = document.getElementById("sectionUsersContainer");
  if (!container) return;

  try {
    const res = await fetch("templates/users.html");
    if (!res.ok) throw new Error(`HTTP error ${res.status}`);
    container.innerHTML = await res.text();
  } catch (err) {
    console.error("❌ Failed to load users.html template:", err);
    container.innerHTML = `<div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">Could not load the users template: ${escapeHTML(err.message)}</div>`;
  }
}

/**
 * Initializes the users controller with Firestore bindings
 */
export function initUsersController(ctx) {
  firestoreCtx = ctx;
  bindFamilyImportDialog(ctx);
  bindFamilyRecipeDrawer(ctx);
  bindFamilyImportBatchDrawer(ctx);
  initTestUsersPanel(ctx);
  const { db, getDoc, doc, setDoc, collection, query, where, getDocs, showToast } = ctx;

  const form = document.getElementById("userSearchForm");
  const input = document.getElementById("userSearchInput");
  const clearBtn = document.getElementById("userSearchClearBtn");

  if (form && input) {
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const term = input.value.trim();
      if (!term) {
        showToast("Please enter a User ID or email address to search.", "info");
        return;
      }
      await performUserSearch(term);
    });

    input.addEventListener("input", () => {
      if (clearBtn) {
        clearBtn.style.display = input.value.trim() ? "inline-flex" : "none";
      }
    });
  }

  if (clearBtn && input) {
    clearBtn.addEventListener("click", () => {
      input.value = "";
      clearBtn.style.display = "none";
      resetUserSearchState();
    });
  }
}

/**
 * Resets search state back to initial empty prompt
 */
export function resetUserSearchState() {
  activeUserRecord = null;
  activeHeatmapFeature = "overall";
  familyCard.reset();
  const area = document.getElementById("userSearchResultArea");
  if (!area) return;
  area.innerHTML = `
    <div class="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center dark:border-white/15">
      ${icon("user-circle", "mx-auto size-12 text-gray-400 dark:text-gray-500")}
      <h3 class="mt-2 text-sm font-semibold text-gray-900 dark:text-white">Search for a user</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Their profile, switches, subscription, family and activity show here.</p>
    </div>
  `;
}

/**
 * Searches for a user via adminLookupUser Cloud Function (with Firestore direct fallback)
 */
export async function performUserSearch(searchTerm) {
  activeHeatmapFeature = "overall";
  familyCard.reset();
  const { db, getDoc, doc, functions, httpsCallable, showToast } = firestoreCtx;
  const area = document.getElementById("userSearchResultArea");
  if (!area) return;

  area.innerHTML = `
    <div class="${RESULT_CARD} px-6 py-10 text-center">
      <span class="mx-auto mb-3 block size-6 animate-spin rounded-full border-2 border-gray-300 border-t-paprika-600 dark:border-white/20 dark:border-t-paprika-400"></span>
      <p class="text-sm font-medium text-gray-900 dark:text-white">Searching for “${escapeHTML(searchTerm)}”…</p>
    </div>
  `;

  try {
    let result = null;

    // 1. Preferred lookup via adminLookupUser Cloud Function (queries Firebase Auth & Firestore)
    if (functions && typeof httpsCallable === "function") {
      try {
        const lookupFn = httpsCallable(functions, "adminLookupUser");
        const res = await lookupFn({ identifier: searchTerm });
        if (res.data && res.data.found) {
          result = res.data;
        }
      } catch (fnErr) {
        console.warn("adminLookupUser callable error:", fnErr);
      }
    }

    // 2. Fallback: Direct Firestore doc lookup by UID
    if (!result) {
      try {
        const docSnap = await getDoc(doc(db, "users", searchTerm));
        if (docSnap.exists()) {
          const d = docSnap.data() || {};
          result = {
            found: true,
            uid: docSnap.id,
            email: d.email || null,
            displayName: d.display_name || d.displayName || d.name || null,
            verbose_diagnostics: Boolean(d.verbose_diagnostics),
            tester_mode: Boolean(d.tester_mode),
            lifetime_sub: Boolean(d.lifetime_sub),
            test_user: d.test_user === true,
            test_user_reasons: Array.isArray(d.test_user_reasons) ? d.test_user_reasons : [],
            created_in: d.created_in || null,
            family_id: d.family_id || d.familyId || null,
            fcm_tokens: d.fcm_tokens || [],
            last_device: d.last_device || null
          };
        }
      } catch (dbErr) {
        console.warn("Direct Firestore lookup error:", dbErr);
      }
    }

    if (!result || !result.found) {
      area.innerHTML = `
        <div class="${RESULT_CARD} px-6 py-10 text-center">
          ${solidIcon("exclamation-triangle", "mx-auto size-8 text-yellow-500")}
          <h3 class="mt-2 text-sm font-semibold text-gray-900 dark:text-white">No such user</h3>
          <p class="mx-auto mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">No Firebase Auth account or Firestore document has <code class="font-mono text-xs">${escapeHTML(searchTerm)}</code> as its ID or email.</p>
          <button type="button" onclick="window.crmApp.resetUserSearch()" class="mt-4 ${BUTTON.secondary}">Clear the search</button>
        </div>
      `;
      showToast("No user found with the specified identifier.", "error");
      return;
    }

    // Fallback: If heatmap was not included in Cloud Function payload, attempt direct client fetch
    if (result && result.found && !result.heatmap && db && typeof getDoc === "function" && typeof doc === "function") {
      try {
        const hmSnap = await getDoc(doc(db, "users", result.uid, "activity_heatmap", "current"));
        if (hmSnap.exists()) {
          result.heatmap = normalizeHeatmapData(hmSnap.data());
        }
      } catch (hmErr) {
        console.warn("Direct activity heatmap lookup error:", hmErr);
      }
    }

    // adminLookupUser nests the subscription's state under `computed` (formatSubscriptionDataForResponse
    // in functions/user_diagnostics_service.js); the cards read it flat (#1208).
    if (result.subscription?.computed) result.subscription = { ...result.subscription, ...result.subscription.computed };

    activeUserRecord = result;
    renderUserResultCard(activeUserRecord);
  } catch (err) {
    console.error("❌ Error querying user:", err);
    area.innerHTML = `
      <div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300"><strong class="font-semibold">The search failed:</strong> ${escapeHTML(err.message)}</div>
    `;
    showToast(`Search error: ${err.message}`, "error");
  }
}

/**
 * Renders the User Result Card with Developer Mode controls
 */
// The card around each block of the result.
const RESULT_CARD = "rounded-lg bg-white shadow-xs outline-1 outline-black/5 dark:bg-gray-800/50 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10";
const PURPLE_BADGE = "inline-flex items-center gap-x-1.5 rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 inset-ring inset-ring-purple-700/10 dark:bg-purple-400/10 dark:text-purple-400 dark:inset-ring-purple-400/30";
const PURPLE_DOT = "size-1.5 rounded-full bg-purple-500";

// A Tailwind Plus switch; aria-checked moves the knob. The click runs the toggle function, which
// disables the switch while it writes.
const SWITCH =
  "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-paprika-600 disabled:cursor-wait disabled:opacity-50 " +
  "aria-checked:bg-paprika-600 dark:bg-white/10 dark:aria-checked:bg-paprika-500";
const SWITCH_KNOB = "pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-xs ring-0 transition duration-200 ease-in-out in-aria-checked:translate-x-5";

/** The yellow badge for a trial or subscription that will not renew (#2485), or "" when it renews. */
function autoRenewOffBadge(sub) {
  const off = autoRenewOff(sub, sub?.event, sub?.raw_payload);
  if (!off) return "";
  const title = `The customer keeps Savvy until it expires, then it ends${off.reason ? ` (${off.reason})` : ""}`;
  return ` <span class="${BADGE.yellow}" title="${escapeHTML(title)}"><span aria-hidden="true" class="${BADGE_DOT.yellow}"></span>Auto-renew off${off.reason ? ` · ${escapeHTML(off.reason)}` : ""}</span>`;
}

function badge(tone, dot, label) {
  return `<span class="${tone}"><span aria-hidden="true" class="${dot}"></span>${label}</span>`;
}

function switchRow(id, on, title, description) {
  return `
    <div class="flex items-center justify-between gap-x-6 py-4">
      <span class="flex grow flex-col">
        <span id="${id}-label" class="text-sm/6 font-medium text-gray-900 dark:text-white">${title}</span>
        <span id="${id}-description" class="text-sm text-gray-500 dark:text-gray-400">${description}</span>
      </span>
      <button type="button" id="${id}" role="switch" aria-checked="${on}" aria-labelledby="${id}-label" aria-describedby="${id}-description" class="${SWITCH}"><span aria-hidden="true" class="${SWITCH_KNOB}"></span></button>
    </div>`;
}

export function renderUserResultCard(user) {
  const area = document.getElementById("userSearchResultArea");
  if (!area) return;

  const isDevMode = Boolean(user.verbose_diagnostics);
  const isTesterMode = Boolean(user.tester_mode);
  const isLifetimeSub = Boolean(user.lifetime_sub);
  const isAdmin = Boolean(user.isAdmin);
  const testReasons = Array.isArray(user.test_user_reasons) ? user.test_user_reasons : [];
  const isMarkedHere = testReasons.includes("manual");
  const displayName = user.display_name || user.displayName || user.name || "Anonymous or guest";
  const email = user.email || "No email linked";
  const familyId = user.family_id || user.familyId || "—";
  const fcmCount = Array.isArray(user.fcm_tokens) ? user.fcm_tokens.length : (user.fcm_token ? 1 : 0);
  const lastDevice = lastDeviceHTML(user.last_device);
  const initials = (user.display_name || user.displayName || user.name || user.email || "?").trim().slice(0, 1).toUpperCase();

  const sub = user.subscription;
  let subBadge = badge(BADGE.gray, BADGE_DOT.gray, "Free plan");
  if (sub && sub.is_active) {
    subBadge = sub.is_trial
      ? badge(PURPLE_BADGE, PURPLE_DOT, `Trial (${escapeHTML(sub.plan_name || "Savvy")})`)
      : badge(BADGE.green, BADGE_DOT.green, `Subscriber (${escapeHTML(sub.plan_name || "Savvy")})`);
    subBadge += autoRenewOffBadge(sub);
  } else if (sub) {
    subBadge = badge(BADGE.red, BADGE_DOT.red, "Subscription expired");
  }
  // Test users (#1922): the marker and a debug build's account, next to the plan.
  if (user.test_user) subBadge += ` <span class="${BADGE.yellow}" title="Left out of analytics">Test user</span>`;
  if (user.created_in === "debug") subBadge += ` <span class="${BADGE.gray}" title="Created with a debug build: the Test users card can delete it">Debug build</span>`;
  // The switches below show admin, developer, tester, test user and lifetime; the header shows only the plan.
  const detail = (label, valueHTML) => `
    <div class="px-4 py-3 sm:px-0">
      <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">${label}</dt>
      <dd class="mt-1 text-sm text-gray-900 dark:text-white">${valueHTML}</dd>
    </div>`;

  area.innerHTML = `
    <div class="${RESULT_CARD}">
      <div class="flex flex-wrap items-start justify-between gap-4 px-4 py-5 sm:px-6">
        <div class="flex min-w-0 items-center gap-4">
          <span class="flex size-12 flex-none items-center justify-center rounded-full bg-paprika-50 text-lg font-semibold text-paprika-700 dark:bg-paprika-500/15 dark:text-paprika-300">${escapeHTML(initials)}</span>
          <div class="min-w-0">
            <h3 class="truncate text-base font-semibold text-gray-900 dark:text-white">${escapeHTML(displayName)}</h3>
            <p class="truncate text-sm text-gray-500 dark:text-gray-400">${escapeHTML(email)}</p>
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-3">${subBadge}
          <a href="${escapeHTML(historyHref({ uid: user.uid }))}" title="Every message to and from this user, newest first" class="${BUTTON.small}">${icon("clock", "size-4 text-gray-400")}Contact history</a>
        </div>
      </div>

      <dl class="grid grid-cols-1 border-t border-gray-100 px-4 sm:grid-cols-3 sm:gap-x-6 sm:px-6 dark:border-white/5">
        ${detail("User ID", `<span class="flex items-center gap-1.5 font-mono text-xs break-all">${escapeHTML(user.uid)}<button type="button" ${copyAttributes(user.uid, "Copied the user ID.")} title="Copy the user ID" class="flex-none rounded-xs p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><span class="sr-only">Copy</span>${solidIcon("clipboard-document", "size-4")}</button></span>`)}
        ${detail("Family ID", familyId === "—" ? "—" : `<a href="${escapeHTML(familyHref(familyId))}" title="Open the family's page" class="${BUTTON.link} font-mono text-xs break-all">${escapeHTML(familyId)}</a>`)}
        ${detail("Devices with push", `${fcmCount} device${fcmCount === 1 ? "" : "s"}`)}
        <div class="sm:col-span-3">${detail("Last device", lastDevice)}</div>
      </dl>

      <div class="divide-y divide-gray-100 border-t border-gray-100 px-4 sm:px-6 dark:divide-white/5 dark:border-white/5">
        ${switchRow("toggleAdminRoleActionBtn", isAdmin, "Administrator", isAdmin
          ? "Full access to this CRM and to every collection the rules guard."
          : "Gives full access to this CRM and to every collection the rules guard.")}
        ${switchRow("toggleDevModeActionBtn", isDevMode, "Developer mode", isDevMode
          ? "The diagnostics tools show in this user's iOS Settings."
          : "Shows the diagnostics tools in this user's iOS Settings.")}
        ${switchRow("toggleTesterModeActionBtn", isTesterMode, "Tester mode", isTesterMode
          ? "This user gets pre-release features in the apps."
          : "Gives this user pre-release features in the apps.")}
        ${switchRow("toggleTestUserActionBtn", isMarkedHere, "Test user", testUserDescription(user.test_user === true, testReasons))}
        ${switchRow("toggleLifetimeSubActionBtn", isLifetimeSub, "Lifetime access", isLifetimeSub
          ? "Every paid feature (AI imports, meal plans, smart transforms), without a store subscription."
          : "Unlocks every paid feature (AI imports, meal plans, smart transforms) for good, without a store subscription.")}
      </div>
    </div>

    <div id="userSubscriptionCardContainer" class="mt-6">
      ${buildSubscriptionCardHTML(user.subscription, user)}
    </div>

    <div id="userFamilyCardContainer" class="mt-6"></div>

    <div id="userHeatmapCardContainer" class="mt-6">
      ${buildHeatmapCardHTML(user.heatmap, activeHeatmapFeature)}
    </div>
  `;

  // Attach button events
  const toggleDevBtn = document.getElementById("toggleDevModeActionBtn");
  if (toggleDevBtn) {
    toggleDevBtn.addEventListener("click", async () => {
      await toggleUserDeveloperMode(user.uid, isDevMode);
    });
  }

  const toggleTesterBtn = document.getElementById("toggleTesterModeActionBtn");
  if (toggleTesterBtn) {
    toggleTesterBtn.addEventListener("click", async () => {
      await toggleUserTesterMode(user.uid, isTesterMode);
    });
  }

  const toggleTestUserBtn = document.getElementById("toggleTestUserActionBtn");
  if (toggleTestUserBtn) {
    toggleTestUserBtn.addEventListener("click", async () => {
      await toggleUserTestUser(user.uid, isMarkedHere);
    });
  }

  const toggleLifetimeBtn = document.getElementById("toggleLifetimeSubActionBtn");
  if (toggleLifetimeBtn) {
    toggleLifetimeBtn.addEventListener("click", async () => {
      await toggleUserLifetimeSub(user.uid, isLifetimeSub);
    });
  }

  const toggleAdminBtn = document.getElementById("toggleAdminRoleActionBtn");
  if (toggleAdminBtn) {
    toggleAdminBtn.addEventListener("click", async () => {
      await toggleUserAdminRole(user.uid, isAdmin);
    });
  }

  attachSubscriptionCardEvents(user.subscription);
  attachHeatmapCardEvents();
  showFamilyCard(user);
}

/**
 * The family card under the subscription. A user without family_id may own a family keyed by their uid (the rules allow
 * that id), so the card reads that one.
 */
function showFamilyCard(user) {
  familyCard.show(user.family_id || user.familyId || user.uid);
}

/**
 * Formats a subscription expiration or renewal date relative to now (e.g. "Renews or expires in 7 days", "Expired 2
 * days ago"); with auto-renew off, "Ends in 7 days" (#2485).
 */
export function formatRelativeSubscriptionTime(rawDate, isActive, autoRenewIsOff = false) {
  if (!rawDate) return "—";
  try {
    let d = null;
    if (typeof rawDate.toDate === "function") {
      d = rawDate.toDate();
    } else if (rawDate && typeof rawDate === "object" && typeof rawDate._seconds === "number") {
      d = new Date(rawDate._seconds * 1000 + (rawDate._nanoseconds || 0) / 1e6);
    } else if (rawDate && typeof rawDate === "object" && typeof rawDate.seconds === "number") {
      d = new Date(rawDate.seconds * 1000 + (rawDate.nanoseconds || 0) / 1e6);
    } else if (typeof rawDate === "string" || typeof rawDate === "number") {
      d = new Date(rawDate);
    }
    if (!d || isNaN(d.getTime())) return "—";

    const diffMs = d.getTime() - Date.now();
    const isFuture = diffMs > 0;
    const absDiffMs = Math.abs(diffMs);
    const minutes = Math.round(absDiffMs / (1000 * 60));
    const hours = Math.round(absDiffMs / (1000 * 60 * 60));
    const days = Math.round(absDiffMs / (1000 * 60 * 60 * 24));

    let timeText = "";
    if (minutes < 60) {
      timeText = `${minutes} min${minutes === 1 ? "" : "s"}`;
    } else if (hours < 24) {
      timeText = `${hours} hr${hours === 1 ? "" : "s"}`;
    } else if (days < 30) {
      timeText = `${days} day${days === 1 ? "" : "s"}`;
    } else {
      const months = Math.round(days / 30);
      timeText = `${months} month${months === 1 ? "" : "s"}`;
    }

    if (isFuture) {
      return autoRenewIsOff ? `Ends in ${timeText} · auto-renew off` : `Renews or expires in ${timeText}`;
    } else {
      return `Expired ${timeText} ago`;
    }
  } catch {
    return "—";
  }
}

/**
 * Builds the In-App Subscription & RevenueCat Diagnostics card HTML
 */
export function buildSubscriptionCardHTML(sub, user) {
  const familyId = user?.family_id || user?.familyId || "—";
  const header = (badgesHTML, subtitleHTML) => `
    <div class="flex flex-wrap items-start justify-between gap-4 border-b border-gray-200 px-4 py-5 sm:px-6 dark:border-white/10">
      <div class="flex items-center gap-3">
        <span class="flex size-10 flex-none items-center justify-center rounded-md bg-paprika-50 text-paprika-600 dark:bg-paprika-500/15 dark:text-paprika-300">${solidIcon("credit-card", "size-5")}</span>
        <div>
          <h3 class="text-base font-semibold text-gray-900 dark:text-white">In-app subscription (RevenueCat)</h3>
          <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">${subtitleHTML}</p>
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-2">${badgesHTML}</div>
    </div>`;

  if (!sub) {
    return `
      <div class="${RESULT_CARD}">
        ${header(`<span class="${BADGE.gray}">Free plan</span>`, `Family <code class="font-mono text-xs">${escapeHTML(familyId)}</code>`)}
        <div class="px-4 py-5 sm:px-6">
          <p class="text-sm font-medium text-gray-900 dark:text-white">No RevenueCat subscription</p>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Nothing at <code class="font-mono text-xs">/families/${escapeHTML(familyId)}/subscription_data/revenuecat_latest</code>, so the free plan's limits apply.</p>
        </div>
      </div>
    `;
  }

  // Status Badge
  let statusBadgeHTML = badge(BADGE.red, BADGE_DOT.red, "Expired");
  if (sub.is_active) {
    statusBadgeHTML = (sub.is_trial ? badge(PURPLE_BADGE, PURPLE_DOT, "Trial") : badge(BADGE.green, BADGE_DOT.green, "Active")) + autoRenewOffBadge(sub);
  }
  const renewsOff = Boolean(autoRenewOff(sub, sub.event, sub.raw_payload));

  // Environment Badge
  const envUpper = (sub.event?.environment || "").toUpperCase();
  const envBadgeHTML = envUpper === "SANDBOX"
    ? `<span class="${BADGE.yellow}">Sandbox</span>`
    : (envUpper === "PRODUCTION" ? `<span class="${BADGE.blue}">Production</span>` : "");

  const storeLabel = sub.store_label || sub.store || "Apple App Store";
  const rawPayload = sub.raw_payload || sub;
  const panel = (title, bodyHTML) => `
    <div class="rounded-lg bg-gray-50 px-4 py-3 dark:bg-white/5">
      <h4 class="text-xs font-medium text-gray-500 dark:text-gray-400">${title}</h4>
      <div class="mt-1 space-y-0.5 text-sm text-gray-500 dark:text-gray-400">${bodyHTML}</div>
    </div>`;
  const strong = (html) => `<span class="font-medium text-gray-900 dark:text-white">${html}</span>`;
  const renewal = sub.event?.renewal_number !== null && sub.event?.renewal_number !== undefined ? sub.event.renewal_number : "—";

  return `
    <div class="${RESULT_CARD}">
      ${header(`${envBadgeHTML}${statusBadgeHTML}<button type="button" id="checkSubOfferBtn" data-uid="${escapeHTML(user?.uid || "")}" title="Ask Apple which offer the next renewal uses, and refresh this user's waitlist leads" class="${BUTTON.small}">${solidIcon("arrow-path", "size-4 text-gray-400")}<span data-label>Check with Apple</span></button><button type="button" id="toggleSubRawJsonBtn" aria-expanded="false" class="${BUTTON.small}">${solidIcon("code-bracket", "size-4 text-gray-400")}<span data-label>Raw document</span></button>`,
        `Family <code class="font-mono text-xs">${escapeHTML(familyId)}</code> · Store ${strong(escapeHTML(storeLabel))}`)}

      <div class="grid grid-cols-1 gap-3 px-4 py-5 sm:grid-cols-2 sm:px-6 xl:grid-cols-4">
        ${panel("Plan and entitlement", `
          <p class="text-base font-semibold text-gray-900 dark:text-white">${escapeHTML(sub.plan_name || "WeeknightBite Savvy")}</p>
          <p>Product <code class="font-mono text-xs text-gray-900 dark:text-white">${escapeHTML(sub.product_id || "—")}</code></p>
          <p>Entitlements ${strong(escapeHTML(sub.event?.entitlement_ids?.join(", ") || "—"))}</p>
          <p>Offer ${strong(escapeHTML(offerText(sub.offer_code) || "—"))}</p>`)}
        ${panel("Price and billing", `
          <p class="text-base font-semibold text-gray-900 dark:text-white">${escapeHTML(sub.formatted_price || "—")} <span class="text-sm font-normal text-gray-500 dark:text-gray-400">(${escapeHTML(sub.term || "annual")})</span></p>
          <p>Currency ${strong(escapeHTML(sub.currency || "USD"))} · Country ${strong(escapeHTML(sub.country_code || "US"))}</p>
          ${sub.manage_url
            ? `<p><a href="${escapeHTML(sub.manage_url)}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-x-1 font-medium text-paprika-700 hover:text-paprika-800 dark:text-paprika-400 dark:hover:text-paprika-300">Manage in Apple Settings${solidIcon("arrow-top-right-on-square", "size-4")}</a></p>`
            : `<p>Platform ${strong(escapeHTML(sub.store_platform || "iOS"))}</p>`}`)}
        ${panel("Period", `
          <p class="text-base font-semibold text-gray-900 dark:text-white">${escapeHTML(sub.event?.period_type || (sub.is_trial ? "TRIAL" : "NORMAL"))}</p>
          <p>Started ${strong(formatHeatmapDate(sub.started_at))}</p>
          <p>Expires ${strong(formatHeatmapDate(sub.expires_at))}</p>
          <p class="${!sub.is_active ? "font-medium text-red-600 dark:text-red-400" : renewsOff ? "font-medium text-yellow-700 dark:text-yellow-500" : "font-medium text-green-700 dark:text-green-400"}">${formatRelativeSubscriptionTime(sub.expires_at, sub.is_active, renewsOff)}</p>`)}
        ${panel("Last webhook", `
          <p class="text-base font-semibold text-gray-900 dark:text-white"><code class="font-mono text-sm">${escapeHTML(sub.event?.type || "—")}</code></p>
          <p class="flex items-center gap-1">Transaction <span class="font-mono text-xs break-all text-gray-900 dark:text-white">${escapeHTML(sub.event?.transaction_id || "—")}</span>${sub.event?.transaction_id
            ? `<button type="button" ${copyAttributes(sub.event.transaction_id, "Copied the transaction ID.")} title="Copy the transaction ID" class="flex-none rounded-xs p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><span class="sr-only">Copy</span>${solidIcon("clipboard-document", "size-4")}</button>`
            : ""}</p>
          <p>Original <span class="font-mono text-xs break-all">${escapeHTML(sub.event?.original_transaction_id || "—")}</span></p>
          <p>Renewal ${strong(`#${renewal}`)}</p>`)}
      </div>

      <div id="subOfferCheckResult" style="display: none;" class="border-t border-gray-200 px-4 py-4 sm:px-6 dark:border-white/10"></div>

      <div id="subRawJsonSection" style="display: none;" class="border-t border-gray-200 px-4 py-4 sm:px-6 dark:border-white/10">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <span class="font-mono text-xs text-gray-500 dark:text-gray-400">/families/${escapeHTML(familyId)}/subscription_data/revenuecat_latest</span>
          <button type="button" id="copySubRawJsonBtn" class="${BUTTON.small}">${solidIcon("clipboard-document", "size-4 text-gray-400")}Copy</button>
        </div>
        <pre id="subRawJsonPre" class="mt-3 max-h-96 overflow-auto rounded-md bg-gray-900 p-3 font-mono text-xs/5 text-gray-100">${escapeHTML(JSON.stringify(rawPayload, null, 2))}</pre>
      </div>
    </div>
  `;
}

/**
 * Toggles visibility of the raw subscription JSON section
 */
export function toggleSubRawJson() {
  const jsonSection = document.getElementById("subRawJsonSection");
  const toggleBtn = document.getElementById("toggleSubRawJsonBtn");
  if (!jsonSection) return;
  const isHidden = jsonSection.style.display === "none";
  jsonSection.style.display = isHidden ? "block" : "none";
  if (toggleBtn) {
    toggleBtn.setAttribute("aria-expanded", String(isHidden));
    const label = toggleBtn.querySelector("[data-label]");
    if (label) label.textContent = isHidden ? "Hide the raw document" : "Raw document";
  }
}

/**
 * Copies the raw subscription payload to clipboard
 */
export function copySubRawJson(showToast) {
  const sub = activeUserRecord?.subscription;
  if (!sub) {
    if (showToast) showToast("No subscription payload available to copy.", "info");
    return;
  }
  const textToCopy = JSON.stringify(sub.raw_payload || sub, null, 2);
  navigator.clipboard.writeText(textToCopy).then(() => {
    if (showToast) {
      showToast("Copied RevenueCat raw payload to clipboard", "info");
    } else if (firestoreCtx?.showToast) {
      firestoreCtx.showToast("Copied RevenueCat raw payload to clipboard", "info");
    }
  }).catch((err) => {
    console.error("Clipboard copy failed:", err);
  });
}

/**
 * Attaches event listeners for the subscription diagnostics card
 */
export function attachSubscriptionCardEvents(sub) {
  const toggleBtn = document.getElementById("toggleSubRawJsonBtn");
  if (toggleBtn) {
    toggleBtn.addEventListener("click", () => {
      toggleSubRawJson();
    });
  }

  const copyBtn = document.getElementById("copySubRawJsonBtn");
  if (copyBtn) {
    copyBtn.addEventListener("click", () => {
      copySubRawJson(firestoreCtx?.showToast);
    });
  }

  const checkOfferBtn = document.getElementById("checkSubOfferBtn");
  if (checkOfferBtn) {
    checkOfferBtn.addEventListener("click", () => checkSubscriptionOffer(checkOfferBtn.dataset.uid));
  }
}

/**
 * Asks `adminRefreshLeadOfferStatus` for the user's household (#1960): which offer the next renewal uses, which only
 * Apple knows during an App Store trial. It refreshes the user's waitlist leads on the way.
 */
export async function checkSubscriptionOffer(uid) {
  const { functions, httpsCallable, showToast } = firestoreCtx || {};
  const btn = document.getElementById("checkSubOfferBtn");
  const result = document.getElementById("subOfferCheckResult");
  if (!functions || !httpsCallable || !uid || !result) return;
  const label = btn?.querySelector("[data-label]");
  if (btn) btn.disabled = true;
  if (label) label.textContent = "Checking…";
  try {
    const { data } = await httpsCallable(functions, "adminRefreshLeadOfferStatus")({ uid });
    const rows = offerStatusRows(data.household);
    const leads = data.leads || [];
    result.innerHTML = `
      <div class="flex flex-wrap items-center gap-2">
        <h4 class="text-sm font-semibold text-gray-900 dark:text-white">Offer at the next renewal</h4>
        ${offerStatusBadgeHTML(data.household)}
      </div>
      <dl class="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
        ${rows.map(([name, value]) => `<dt class="text-gray-500 dark:text-gray-400">${escapeHTML(name)}</dt><dd class="break-all text-gray-900 dark:text-white">${escapeHTML(value)}</dd>`).join("")}
      </dl>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">${leads.length
        ? `Waitlist lead${leads.length === 1 ? "" : "s"} refreshed: ${leads.map((l) => escapeHTML(l.lead_id)).join(", ")}.`
        : "No waitlist lead is linked to this user."}</p>`;
    result.style.display = "block";
  } catch (err) {
    console.error("❌ Failed to check the offer with Apple:", err);
    showToast?.(`Couldn't check with Apple: ${err.message}`, "error");
  } finally {
    if (btn) btn.disabled = false;
    if (label) label.textContent = "Check with Apple";
  }
}

/**
 * Grants or revokes Administrator custom claim for a user
 */
export async function toggleUserAdminRole(uid, currentAdminStatus) {
  const { functions, httpsCallable, showToast } = firestoreCtx;
  const toggleBtn = document.getElementById("toggleAdminRoleActionBtn");
  if (toggleBtn) toggleBtn.disabled = true;

  const nextAdminStatus = !currentAdminStatus;
  const actionName = nextAdminStatus ? "GRANT" : "REVOKE";
  const userIdentifier = activeUserRecord?.email || uid;

  const confirmed = window.confirm(
    `Are you sure you want to ${actionName} Administrator privileges for '${userIdentifier}'?`
  );

  if (!confirmed) {
    if (toggleBtn) toggleBtn.disabled = false;
    return;
  }

  try {
    const setAdminRoleFn = httpsCallable(functions, "adminSetUserAdminRole");
    const resp = await setAdminRoleFn({ uid, admin: nextAdminStatus });

    if (activeUserRecord && activeUserRecord.uid === uid) {
      activeUserRecord.isAdmin = nextAdminStatus;
      renderUserResultCard(activeUserRecord);
    }

    const toastMessage = nextAdminStatus
      ? `👑 Admin privileges GRANTED to ${userIdentifier}`
      : `🚫 Admin privileges REVOKED from ${userIdentifier}`;
    showToast(toastMessage, "success");
  } catch (err) {
    console.error("❌ Failed to toggle admin role:", err);
    showToast(`Failed to update admin role: ${err.message}`, "error");
    if (toggleBtn) toggleBtn.disabled = false;
  }
}

/**
 * Toggles the verbose_diagnostics boolean on the user document in Firestore
 */
export async function toggleUserDeveloperMode(uid, currentStatus) {
  const { db, setDoc, doc, showToast } = firestoreCtx;
  const toggleBtn = document.getElementById("toggleDevModeActionBtn");
  if (toggleBtn) toggleBtn.disabled = true;

  const nextStatus = !currentStatus;

  try {
    const userRef = doc(db, "users", uid);
    await setDoc(userRef, {
      verbose_diagnostics: nextStatus,
      updated_at: new Date().toISOString()
    }, { merge: true });

    if (activeUserRecord && activeUserRecord.uid === uid) {
      activeUserRecord.verbose_diagnostics = nextStatus;
      renderUserResultCard(activeUserRecord);
    }

    const toastMessage = nextStatus
      ? `⚡ Developer Mode enabled for user ${uid}`
      : `🛑 Developer Mode disabled for user ${uid}`;
    showToast(toastMessage, "success");
  } catch (err) {
    console.error("❌ Failed to update verbose_diagnostics for user:", err);
    showToast(`Failed to update developer mode: ${err.message}`, "error");
    if (toggleBtn) {
      toggleBtn.disabled = false;
    }
  }
}

/**
 * Toggles the tester_mode boolean on the user document in Firestore
 */
export async function toggleUserTesterMode(uid, currentStatus) {
  const { db, setDoc, doc, showToast } = firestoreCtx;
  const toggleBtn = document.getElementById("toggleTesterModeActionBtn");
  if (toggleBtn) toggleBtn.disabled = true;

  const nextStatus = !currentStatus;

  try {
    const userRef = doc(db, "users", uid);
    await setDoc(userRef, {
      tester_mode: nextStatus,
      updated_at: new Date().toISOString()
    }, { merge: true });

    if (activeUserRecord && activeUserRecord.uid === uid) {
      activeUserRecord.tester_mode = nextStatus;
      renderUserResultCard(activeUserRecord);
    }

    const toastMessage = nextStatus
      ? `🧪 Tester Mode enabled for user ${uid}`
      : `🛑 Tester Mode disabled for user ${uid}`;
    showToast(toastMessage, "success");
  } catch (err) {
    console.error("❌ Failed to update tester_mode for user:", err);
    showToast(`Failed to update tester mode: ${err.message}`, "error");
    if (toggleBtn) {
      toggleBtn.disabled = false;
    }
  }
}

/**
 * The Test user switch's line: whether analytics leave the account out, and why (#1922). The switch is only the
 * "Marked here" reason; the others come from builds and the list, and stay when it is off.
 */
export function testUserDescription(isTestUser, reasons = []) {
  const others = reasons.filter((reason) => reason !== "manual");
  if (!isTestUser && !reasons.length) return "Leaves this account out of analytics, as a test user.";
  const why = reasons.length ? ` Why: ${reasonBadgesHTML(reasons)}` : "";
  const note = reasons.includes("manual") && others.length ? " Switching off removes only Marked here." : "";
  return `Analytics leave this account out.${why}${note}`;
}

/**
 * Adds or removes the "manual" reason on users/{uid}.test_user_reasons (#1922). The onUserWritten function sets
 * test_user from the reasons, so only the reason is written here.
 */
export async function toggleUserTestUser(uid, currentlyMarked) {
  const { db, setDoc, doc, arrayUnion, arrayRemove, showToast } = firestoreCtx;
  const toggleBtn = document.getElementById("toggleTestUserActionBtn");
  if (toggleBtn) toggleBtn.disabled = true;
  const mark = !currentlyMarked;
  try {
    await setDoc(doc(db, "users", uid), {
      test_user_reasons: mark ? arrayUnion("manual") : arrayRemove("manual"),
      updated_at: new Date().toISOString()
    }, { merge: true });
    if (activeUserRecord && activeUserRecord.uid === uid) {
      const reasons = (activeUserRecord.test_user_reasons || []).filter((reason) => reason !== "manual");
      if (mark) reasons.push("manual");
      activeUserRecord.test_user_reasons = reasons;
      activeUserRecord.test_user = reasons.length > 0;
      renderUserResultCard(activeUserRecord);
    }
    showToast(mark ? `Marked ${uid} as a test user.` : `Removed Marked here from ${uid}.`, "success");
  } catch (err) {
    console.error("❌ Failed to update test_user_reasons for user:", err);
    showToast(`Failed to update the test user mark: ${err.message}`, "error");
    if (toggleBtn) toggleBtn.disabled = false;
  }
}

/**
 * Toggles the lifetime_sub boolean on the user document in Firestore
 */
export async function toggleUserLifetimeSub(uid, currentStatus) {
  const { db, setDoc, doc, showToast } = firestoreCtx;
  const toggleBtn = document.getElementById("toggleLifetimeSubActionBtn");
  if (toggleBtn) toggleBtn.disabled = true;

  const nextStatus = !currentStatus;

  try {
    const userRef = doc(db, "users", uid);
    await setDoc(userRef, {
      lifetime_sub: nextStatus,
      updated_at: new Date().toISOString()
    }, { merge: true });

    if (activeUserRecord && activeUserRecord.uid === uid) {
      activeUserRecord.lifetime_sub = nextStatus;
      renderUserResultCard(activeUserRecord);
    }

    const toastMessage = nextStatus
      ? `💎 Lifetime Subscription granted for user ${uid}`
      : `🛑 Lifetime Subscription revoked for user ${uid}`;
    showToast(toastMessage, "success");
  } catch (err) {
    console.error("❌ Failed to update lifetime_sub for user:", err);
    showToast(`Failed to update lifetime subscription: ${err.message}`, "error");
    if (toggleBtn) {
      toggleBtn.disabled = false;
    }
  }
}

function escapeHTML(str) {
  if (typeof str !== "string") return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Formats a 0-23 hour number into 12-hour AM/PM string (e.g. 0 -> "12 AM", 15 -> "3 PM")
 */
export function formatHour(h) {
  const hourNum = parseInt(h, 10);
  if (isNaN(hourNum)) return "—";
  if (hourNum === 0) return "12 AM";
  if (hourNum < 12) return `${hourNum} AM`;
  if (hourNum === 12) return "12 PM";
  return `${hourNum - 12} PM`;
}

/**
 * Formats a 0-23 hour number into compact short label (e.g. 0 -> "12a", 15 -> "3p")
 */
export function formatHourShort(h) {
  const hourNum = parseInt(h, 10);
  if (isNaN(hourNum)) return "";
  if (hourNum === 0) return "12a";
  if (hourNum === 12) return "12p";
  if (hourNum < 12) return `${hourNum}a`;
  return `${hourNum - 12}p`;
}

/**
 * Formats an ISO date, Firestore Timestamp, or epoch into a readable string
 */
export function formatHeatmapDate(rawDate) {
  if (!rawDate) return "—";
  try {
    let d = null;
    if (typeof rawDate.toDate === "function") {
      d = rawDate.toDate();
    } else if (rawDate && typeof rawDate === "object" && typeof rawDate._seconds === "number") {
      d = new Date(rawDate._seconds * 1000 + (rawDate._nanoseconds || 0) / 1e6);
    } else if (rawDate && typeof rawDate === "object" && typeof rawDate.seconds === "number") {
      d = new Date(rawDate.seconds * 1000 + (rawDate.nanoseconds || 0) / 1e6);
    } else if (typeof rawDate === "string" || typeof rawDate === "number") {
      d = new Date(rawDate);
    }
    if (!d || isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return "—";
  }
}

/**
 * Normalizes activity heatmap data from various formats (nested maps or flattened dotted keys).
 */
export function normalizeHeatmapData(rawData) {
  if (!rawData || typeof rawData !== "object") {
    return {
      heatmaps: {},
      total_events_count: 0,
      user_timezone: "UTC",
      is_mature: false,
      updated_at: null,
      first_event_at: null
    };
  }

  const result = {
    heatmaps: {},
    total_events_count: Number(rawData.total_events_count) || 0,
    user_timezone: rawData.user_timezone || "UTC",
    is_mature: Boolean(rawData.is_mature),
    updated_at: rawData.updated_at || null,
    first_event_at: rawData.first_event_at || null
  };

  // 1. If rawData already has a nested .heatmaps object
  if (rawData.heatmaps && typeof rawData.heatmaps === "object") {
    for (const [featKey, featVal] of Object.entries(rawData.heatmaps)) {
      if (!result.heatmaps[featKey]) result.heatmaps[featKey] = {};
      if (featVal && typeof featVal === "object") {
        for (const [dayKey, dayVal] of Object.entries(featVal)) {
          const d = dayKey.toLowerCase();
          if (!result.heatmaps[featKey][d]) {
            result.heatmaps[featKey][d] = {};
          }
          if (dayVal && typeof dayVal === "object") {
            for (const [hourKey, count] of Object.entries(dayVal)) {
              result.heatmaps[featKey][d][String(hourKey)] = Number(count) || 0;
            }
          }
        }
      }
    }
  }

  // 2. Parse flattened dotted keys (e.g. "heatmaps.app_open.mon.14": 22 or "app_open.mon.14": 22)
  for (const [key, value] of Object.entries(rawData)) {
    let parts = key.split(".");
    if (parts[0] === "heatmaps") {
      parts.shift();
    }
    if (parts.length === 3) {
      const [feature, day, hour] = parts;
      const f = feature;
      const d = day.toLowerCase();
      const h = String(hour);
      const count = Number(value) || 0;

      if (!result.heatmaps[f]) result.heatmaps[f] = {};
      if (!result.heatmaps[f][d]) result.heatmaps[f][d] = {};
      result.heatmaps[f][d][h] = (result.heatmaps[f][d][h] || 0) + count;
    }
  }

  return result;
}

/**
 * Reads count for a given feature, day ("mon".."sun"), and hour (0..23).
 * If featureId === "overall", aggregates across all features.
 */
export function getHeatmapCellCount(heatmapData, featureId, dayKey, hourNum) {
  if (!heatmapData || !heatmapData.heatmaps) return 0;
  const hourStr = String(hourNum);
  const dayStr = dayKey.toLowerCase();

  if (featureId === "overall") {
    if (heatmapData.heatmaps.overall && heatmapData.heatmaps.overall[dayStr] && typeof heatmapData.heatmaps.overall[dayStr][hourStr] === "number") {
      return heatmapData.heatmaps.overall[dayStr][hourStr];
    }
    let sum = 0;
    for (const feat of Object.keys(heatmapData.heatmaps)) {
      if (feat === "overall") continue;
      const featDays = heatmapData.heatmaps[feat];
      if (featDays && featDays[dayStr] && typeof featDays[dayStr][hourStr] === "number") {
        sum += featDays[dayStr][hourStr];
      }
    }
    return sum;
  }

  const featureMap = heatmapData.heatmaps[featureId];
  if (featureMap && featureMap[dayStr] && typeof featureMap[dayStr][hourStr] === "number") {
    return featureMap[dayStr][hourStr];
  }
  return 0;
}

/**
 * The class list for a heatmap cell, by its share of the peak: grey for none, then four steps of
 * paprika (the legend uses the same steps).
 */
export const HEATMAP_STEPS = [
  { max: 0, cls: "bg-gray-100 text-gray-400 dark:bg-white/5 dark:text-gray-500", label: "No events" },
  { max: 0.25, cls: "bg-paprika-100 text-paprika-900 dark:bg-paprika-500/20 dark:text-paprika-200", label: "Up to a quarter of the peak" },
  { max: 0.5, cls: "bg-paprika-300 text-paprika-950 dark:bg-paprika-500/45 dark:text-white", label: "Up to half of the peak" },
  { max: 0.75, cls: "bg-paprika-500 text-white dark:bg-paprika-500/70", label: "Up to three quarters of the peak" },
  { max: 1, cls: "bg-paprika-700 text-white dark:bg-paprika-500", label: "Near the peak" }
];

export function getIntensityClass(count, maxCount) {
  if (!count || maxCount === 0) return HEATMAP_STEPS[0].cls;
  const ratio = count / maxCount;
  return (HEATMAP_STEPS.find((step) => step.max > 0 && ratio <= step.max) || HEATMAP_STEPS[HEATMAP_STEPS.length - 1]).cls;
}

/**
 * Generates the HTML string for the Activity Heatmap card
 */
export function buildHeatmapCardHTML(rawHeatmapData, activeFeature = "overall") {
  const heatmapData = normalizeHeatmapData(rawHeatmapData);
  const selectedFeature = HEATMAP_FEATURES.find((f) => f.id === activeFeature) || HEATMAP_FEATURES[0];

  // Compute peak count and total events for selected feature
  let maxCount = 0;
  let totalFeatureEvents = 0;
  let peakDay = null;
  let peakHour = null;

  HEATMAP_DAYS.forEach((day) => {
    for (let h = 0; h < 24; h++) {
      const c = getHeatmapCellCount(heatmapData, activeFeature, day.key, h);
      totalFeatureEvents += c;
      if (c > maxCount) {
        maxCount = c;
        peakDay = day.label;
        peakHour = h;
      }
    }
  });

  // Check if any activity events exist across any category in the heatmap
  let hasAnyEventsRecorded = (heatmapData.total_events_count > 0);
  if (!hasAnyEventsRecorded && heatmapData.heatmaps) {
    for (const feat of Object.keys(heatmapData.heatmaps)) {
      for (const d of Object.keys(heatmapData.heatmaps[feat] || {})) {
        for (const h of Object.keys(heatmapData.heatmaps[feat][d] || {})) {
          if ((heatmapData.heatmaps[feat][d][h] || 0) > 0) {
            hasAnyEventsRecorded = true;
            break;
          }
        }
        if (hasAnyEventsRecorded) break;
      }
      if (hasAnyEventsRecorded) break;
    }
  }

  const hasData = Boolean(rawHeatmapData && hasAnyEventsRecorded);
  const totalOverallEvents = heatmapData.total_events_count > 0
    ? heatmapData.total_events_count
    : (activeFeature === "overall" ? totalFeatureEvents : getHeatmapCellCount(heatmapData, "overall"));
  const timezone = heatmapData.user_timezone || "UTC";
  const isMature = Boolean(heatmapData.is_mature);
  const updatedAt = formatHeatmapDate(heatmapData.updated_at);

  // The feature selector: a segmented control, the chosen one with aria-pressed
  const featureButtonsHTML = HEATMAP_FEATURES.map((feat) => `
    <button type="button" data-feature-id="${feat.id}" aria-pressed="${feat.id === activeFeature}" class="heatmap-feature-btn ${SEGMENT.button}">${escapeHTML(feat.label)}</button>
  `).join("");

  const maturityBadgeHTML = isMature
    ? badge(BADGE.green, BADGE_DOT.green, "Settled (3+ weeks)")
    : badge(BADGE.yellow, BADGE_DOT.yellow, "Still calibrating");

  let insightBannerHTML = "";
  if (maxCount > 0) {
    insightBannerHTML = `
      <div class="flex flex-wrap items-center justify-between gap-2 rounded-md bg-gray-50 px-4 py-3 text-sm text-gray-700 dark:bg-white/5 dark:text-gray-300">
        <span class="inline-flex items-center gap-x-1.5">${solidIcon("bolt", "size-4 text-paprika-500")}Peak: <strong class="font-semibold text-gray-900 dark:text-white">${peakDay} at ${formatHour(peakHour)}</strong> (${maxCount} event${maxCount === 1 ? "" : "s"})</span>
        <span class="text-gray-500 dark:text-gray-400"><strong class="font-semibold text-gray-900 dark:text-white">${totalFeatureEvents}</strong> events in ${escapeHTML(selectedFeature.label)}</span>
      </div>
    `;
  } else if (hasData) {
    insightBannerHTML = `
      <div class="rounded-md border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500 dark:border-white/15 dark:text-gray-400">
        No events in <strong class="font-semibold text-gray-900 dark:text-white">${escapeHTML(selectedFeature.label)}</strong> yet; ${totalOverallEvents} across the other features.
      </div>
    `;
  }

  const emptyNoticeHTML = !hasData
    ? `
      <div class="flex gap-3 rounded-md bg-yellow-50 p-4 text-sm text-yellow-800 outline-1 -outline-offset-1 outline-yellow-600/20 dark:bg-yellow-500/10 dark:text-yellow-200 dark:outline-yellow-500/20">
        ${solidIcon("exclamation-triangle", "size-5 flex-none text-yellow-500")}
        <p><strong class="font-semibold">No activity recorded yet.</strong> As the user cooks, plans meals and shops, their weekly pattern collects here.</p>
      </div>
    `
    : "";

  const hourHeaders = Array.from({ length: 24 }).map((_, h) =>
    `<div title="${formatHour(h)}" class="text-center font-mono text-[0.65rem] text-gray-400 dark:text-gray-500">${formatHourShort(h)}</div>`).join("");
  const dayRows = HEATMAP_DAYS.map((day) => `
    <div class="grid grid-cols-[2.75rem_repeat(24,minmax(0,1fr))] items-center gap-1">
      <div class="text-xs font-semibold text-gray-600 dark:text-gray-300">${day.label}</div>
      ${Array.from({ length: 24 }).map((_, h) => {
        const count = getHeatmapCellCount(heatmapData, activeFeature, day.key, h);
        const tooltip = `${day.label} at ${formatHour(h)}: ${count} event${count === 1 ? "" : "s"}`;
        return `<div title="${tooltip}" data-day="${day.key}" data-hour="${h}" data-count="${count}" class="heatmap-cell flex aspect-square min-h-5 items-center justify-center rounded-sm text-[0.65rem] font-semibold transition select-none hover:z-10 hover:scale-125 hover:shadow-md ${getIntensityClass(count, maxCount)}">${count > 0 ? count : ""}</div>`;
      }).join("")}
    </div>
  `).join("");

  return `
    <div class="${RESULT_CARD}">
      <div class="flex flex-wrap items-start justify-between gap-4 border-b border-gray-200 px-4 py-5 sm:px-6 dark:border-white/10">
        <div>
          <div class="flex flex-wrap items-center gap-2">
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">Weekly activity</h3>
            ${maturityBadgeHTML}
          </div>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">7 days by 24 hours, in the user's time zone (<code class="font-mono text-xs">${escapeHTML(timezone)}</code>); ${totalOverallEvents} events recorded.</p>
        </div>
        <p class="text-sm text-gray-500 dark:text-gray-400">Updated ${strongText(escapeHTML(updatedAt))}</p>
      </div>

      <div class="flex flex-col gap-4 px-4 py-5 sm:px-6">
        <div class="${SEGMENT.group} flex-wrap self-start" role="group" aria-label="Feature">${featureButtonsHTML}</div>
        ${emptyNoticeHTML}
        ${insightBannerHTML}
        <div class="overflow-x-auto pb-2">
          <div class="flex min-w-[46rem] flex-col gap-1">
            <div class="grid grid-cols-[2.75rem_repeat(24,minmax(0,1fr))] items-center gap-1">
              <div class="text-[0.65rem] font-semibold text-gray-400 uppercase dark:text-gray-500">Day</div>
              ${hourHeaders}
            </div>
            ${dayRows}
          </div>
        </div>
        <div class="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <span>Less</span>
          ${HEATMAP_STEPS.map((step) => `<span title="${step.label}" class="size-3.5 rounded-sm ${step.cls}"></span>`).join("")}
          <span>More</span>
        </div>
      </div>
    </div>
  `;
}

function strongText(html) {
  return `<span class="font-medium text-gray-900 dark:text-white">${html}</span>`;
}

/**
 * Attaches click event listeners to heatmap feature switcher buttons
 */
export function attachHeatmapCardEvents() {
  const container = document.getElementById("userHeatmapCardContainer");
  if (!container) return;

  const buttons = container.querySelectorAll(".heatmap-feature-btn");
  buttons.forEach((btn) => {
    btn.addEventListener("click", () => {
      const featureId = btn.getAttribute("data-feature-id");
      if (!featureId || featureId === activeHeatmapFeature) return;
      activeHeatmapFeature = featureId;
      container.innerHTML = buildHeatmapCardHTML(activeUserRecord?.heatmap, activeHeatmapFeature);
      attachHeatmapCardEvents();
    });
  });
}


